import {
  defaultLoadableState,
  type LoadableState,
  useLoadableState
} from '@carvana/merch-react-hooks';
import deepmerge from 'deepmerge';
import { createContext, useContext, useMemo, useState } from 'react';
import type React from 'react';
import type { VehicleRating } from '~/contracts/vdp';
import type { DeepPartial } from '~/types';

export type ContextType = {
  experimentsLoaded: boolean;
  experimentsWithBucket: Record<string, string>;
  hasOwnerReviews: LoadableState<boolean>;
  headerHeight: number;
  headerLoaded: boolean;
  isLazyLoadEnabled: boolean;
  setExperimentsLoaded: React.Dispatch<React.SetStateAction<boolean>>;
  setExperimentsWithBucket: React.Dispatch<
    React.SetStateAction<Record<string, string>>
  >;
  setHeaderHeight: React.Dispatch<React.SetStateAction<number>>;
  setHeaderLoaded: React.Dispatch<React.SetStateAction<boolean>>;
  setLazyLoadEnabled: React.Dispatch<React.SetStateAction<boolean>>;
  vehicleRating: LoadableState<VehicleRating>;
};

const defaultContext: ContextType = {
  experimentsLoaded: false,
  experimentsWithBucket: {},
  hasOwnerReviews: defaultLoadableState(false),
  headerHeight: 112,
  headerLoaded: false,
  isLazyLoadEnabled: false,
  setExperimentsLoaded() {},
  setExperimentsWithBucket() {},
  setHeaderHeight() {},
  setHeaderLoaded() {},
  setLazyLoadEnabled() {},
  vehicleRating: defaultLoadableState<VehicleRating>({
    ratingDistribution: [],
    totalRatingCounts: 0
  })
};

const GlobalContext = createContext(defaultContext);

export type OwnProps = React.PropsWithChildren<{
  isLazyLoadEnabled: boolean;
}>;

export function GlobalContextProvider({ children, ...props }: OwnProps) {
  const [experimentsLoaded, setExperimentsLoaded] = useState(
    defaultContext.experimentsLoaded
  );
  const [experimentsWithBucket, setExperimentsWithBucket] = useState(
    defaultContext.experimentsWithBucket
  );
  const hasOwnerReviews = useLoadableState(defaultContext.hasOwnerReviews.data);
  const [headerHeight, setHeaderHeight] = useState<number>(
    defaultContext.headerHeight
  );
  const [headerLoaded, setHeaderLoaded] = useState(defaultContext.headerLoaded);
  const [isLazyLoadEnabled, setLazyLoadEnabled] = useState(
    props.isLazyLoadEnabled
  );
  const vehicleRating = useLoadableState(defaultContext.vehicleRating.data);

  const value = useMemo<ContextType>(
    () => ({
      experimentsLoaded,
      experimentsWithBucket,
      hasOwnerReviews,
      headerHeight,
      headerLoaded,
      isLazyLoadEnabled,
      setExperimentsLoaded,
      setExperimentsWithBucket,
      setHeaderHeight,
      setHeaderLoaded,
      setLazyLoadEnabled,
      vehicleRating
    }),
    [
      experimentsLoaded,
      experimentsWithBucket,
      hasOwnerReviews,
      headerHeight,
      headerLoaded,
      isLazyLoadEnabled,
      vehicleRating
    ]
  );

  return (
    <GlobalContext.Provider value={value}>{children}</GlobalContext.Provider>
  );
}

GlobalContextProvider.displayName = 'GlobalContextProvider';

export const generateGlobalContext = (
  customState: DeepPartial<ContextType>
): ContextType =>
  deepmerge<ContextType, DeepPartial<ContextType>>(defaultContext, customState);

export function useHasOwnerReviews() {
  const { hasOwnerReviews, vehicleRating } = useContext(GlobalContext);

  return hasOwnerReviews.data || vehicleRating.data.totalRatingCounts > 2;
}

export function useVehicleRating() {
  return useContext(GlobalContext).vehicleRating.data;
}

export default GlobalContext;
